/**
 * Texts of an exhausted token quota from the server's grace data (FEAT-054 TASK-011, design D).
 *
 * Sources: 429 `details` (grace_mode, grace_pct as STRINGS, reset_at) and SSE `quota_exceeded`
 * (grace_pct as a NUMBER). Without a readable grace_mode (old server) nothing is built and the
 * caller keeps the server text / the legacy banner.
 */

import type { IntlShape } from 'react-intl';
import { ORG_TIME_ZONE } from '@/utils/formatters';

export interface QuotaExceededInfo {
  graceMode: 'after' | 'before';
  /** Integer 0..50 */
  gracePct: number;
  /** Epoch ms of the renewal, or null when missing/unreadable */
  resetMs: number | null;
}

/** i18n descriptor (same shape as the store's ExceededBannerDescriptor). */
export interface QuotaTextDescriptor {
  messageId: string;
  values?: Record<string, string | number>;
}

const MAX_GRACE_PCT = 50;

function toPct(value: unknown): number | null {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof n !== 'number' || !Number.isFinite(n)) return null;
  const pct = Math.round(n);
  return pct >= 0 && pct <= MAX_GRACE_PCT ? pct : null;
}

/** Info from raw fields (429 details or SSE data); null when grace_mode/grace_pct are not readable. */
export function parseQuotaExceededInfo(raw: {
  graceMode?: unknown;
  gracePct?: unknown;
  resetAt?: unknown;
}): QuotaExceededInfo | null {
  const { graceMode } = raw;
  if (graceMode !== 'after' && graceMode !== 'before') return null;
  const gracePct = toPct(raw.gracePct);
  if (gracePct === null) return null;
  const ms = typeof raw.resetAt === 'string' ? Date.parse(raw.resetAt) : Number.NaN;
  return { graceMode, gracePct, resetMs: Number.isNaN(ms) ? null : ms };
}

/** Descriptor for design D.1: key by mode/percentage, NoDate when there is no valid date. */
export function quotaExceededDescriptor(info: QuotaExceededInfo): QuotaTextDescriptor {
  const base =
    info.gracePct === 0
      ? 'quota.errors.exhausted'
      : info.graceMode === 'after'
        ? 'quota.errors.exhaustedAfter'
        : 'quota.errors.exhaustedBefore';
  const values: Record<string, string | number> = { pct: info.gracePct, usable: 100 - info.gracePct };
  if (info.resetMs === null) return { messageId: `${base}NoDate`, values };
  return { messageId: base, values: { ...values, date: info.resetMs } };
}

/** Replaces a numeric `date` (epoch ms) by the short date and time in the org time zone. */
export function withFormattedDate(
  descriptor: QuotaTextDescriptor,
  intl: Pick<IntlShape, 'formatDate'>,
): QuotaTextDescriptor {
  const date = descriptor.values?.date;
  if (typeof date !== 'number' || !descriptor.messageId.startsWith('quota.errors.')) return descriptor;
  return {
    ...descriptor,
    values: {
      ...descriptor.values,
      date: intl.formatDate(date, { dateStyle: 'short', timeStyle: 'short', timeZone: ORG_TIME_ZONE }),
    },
  };
}

/** Final text, or null when the bundle has no such key (bundle not merged yet): keep the server text. */
export function formatQuotaExceededText(
  info: QuotaExceededInfo,
  intl: Pick<IntlShape, 'formatMessage' | 'formatDate' | 'messages'>,
): string | null {
  const d = withFormattedDate(quotaExceededDescriptor(info), intl);
  if (!(d.messageId in intl.messages)) return null;
  return intl.formatMessage({ id: d.messageId }, d.values);
}
